import { useCallback } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Loading from 'react-loading';

import Breadcrumbs from '@/components/Breadcrumbs';
import Button from '@/components/Button';
import Filters from '@/components/Filters';
import Grid, { Column } from '@/components/Grid';
import Pagination from '@/components/Pagination';
import Search from '@/components/Search';
import Table from '@/components/Table';

import { DashboardProvider, useDashboard } from '@/hooks/useDashboard';
import { useMe } from '@/hooks/useMe';
import { useModal } from '@/hooks/useModal';
import { createCpp, getCpp } from '@/services/webservices/cpp';
import { dashboardColumns } from '@/definitions/dashboardTable';
import { isCreateCppAllowed } from "@/definitions/userRoles";

function DashboardTable() {
	const { isConfirmed } = useModal();
	const { isLoggedIn, subAgentNumber, role } = useMe();
	const { push } = useRouter();

	const createCppConfirm = useCallback( async ( agentData: { agentId: string }, effectiveDate: string, isMidterm: boolean ) => {
		let agentValue = agentData;

		if ( !agentValue ) {
			agentValue = {
				agentId: subAgentNumber
			};
		}

		try {
			const createRequest = await createCpp( agentValue, isMidterm, effectiveDate );

			if ( createRequest.id ) {
				let applicationId;
	
				const cppDetails = await getCpp( createRequest.id );
	
				if ( cppDetails.applications ) {
					applicationId = cppDetails.applications[ 0 ].id;
				}
	
				if ( applicationId ) {
					push( `/cpp/form/?cppId=${ createRequest.id }&formId=${ applicationId }&isNew=true` );
				} else {
					push( `/cpp/?cppId=${ createRequest.id }` );
				}
			}
		} catch ( e ) {
			throw new Error( 'Error saving the application' );
		}
	}, [ push, subAgentNumber ] )

	const createCppClick = useCallback( async( isMidterm: boolean ) => {
		await isConfirmed({
			type: 'createcpp',
			onConfirm: createCppConfirm,
			isMidterm,
		});
	}, [ createCppConfirm, isConfirmed ] );

	const {
		filters,
		page,
		pageSize,
		setPage,
		setPageSize,
		searchString,
		setSearchString,
		sorting,
		setSorting,
		dashboardData,
		isDashboardLoading
	} = useDashboard();

	const handlePageChange = ( event: any ) => {
		setPage( event.selected );
	};

	return (
		<>
			<Head>
				<title>Application Dashboard | Rural Mutual Insurance Company</title>
			</Head>

			<div className="flex gap-x-8 items-center mb-8">
				<div className="flex-auto max-w-[65%]">
					<Search
						searchStr={ searchString }
						onSubmit={ ( data: any ) => setSearchString( data.recordSearch ) }
						onCancel={ () => setSearchString( '' ) }
					/>
				</div>

				{ ( isLoggedIn && isCreateCppAllowed( role ) ) && (
					<div className="flex gap-x-8 ml-auto">
						<Button
							variant="primary"
							size="large"
							onClick={ () => createCppClick( false ) }
						>
							Create new CPP
						</Button>
						
						<Button
							variant="outline"
							size="large"
							onClick={ () => createCppClick( true ) }
						>
							Amend Existing CPP
						</Button>
					</div>
				)}
			</div>

			<Grid className="mb-5">
				<Column columns={ 12 }>
					<Filters />
				</Column>
			</Grid>

			<div className="relative">
				{ isDashboardLoading && (
					<div className="absolute -top-2 -bottom-2 -right-2 -left-2 flex items-center justify-center bg-slate-200/80">
						<Loading type="spin" color="#676767" width={ 64 } height={ 64 } />
					</div>
				)}

				<Grid className="mb-3">
					<Column columns={ 12 }>
						<div className="font-bold text-rural-gray-800">
							{ dashboardData.totalCount } record{ `${ dashboardData.totalCount === 1 ? '' : 's' }` }
							{ searchString && <> match{ `${ dashboardData.totalCount === 1 ? 'es' : '' }` } { `"${ searchString }"` }</>}
						</div>
					</Column>
				</Grid>

				<Table
					data={ dashboardData.applications }
					columns={ dashboardColumns }
					sorting={ sorting }
					setSorting={ setSorting }
				/>

				{ dashboardData.totalCount > 0 && (
					<Grid>
						<Column columns={ 6 }>
							<label className="flex items-center gap-2">
								<select
									id="records-per-page"
									className="border border-rural-gray-300 bg-white py-1 px-2 rounded-sm"
									defaultValue={ pageSize }
									onChange={ ( event: any ) => {
										setPageSize( event.target.value );
									}}
								>
									<option>10</option>
									<option>15</option>
									<option>20</option>
									<option>50</option>
								</select>

								<span className="text-sm font-thin">per page</span>
							</label>
						</Column>
						
						<Column columns={ 6 } className="ml-auto">
							<Pagination
								currentPage={ page }
								onPageChange={ handlePageChange }
								pageSize={ pageSize }
								totalRecords={ dashboardData.totalCount }
							/>
						</Column>
					</Grid>
				)}
			</div>
		</>
	);
}

export default function Dashboard() {
	return (
		<>
			<Breadcrumbs
				links={[
					{ text: 'Dashboard' }
				]}
			/>

			<DashboardProvider>
				<DashboardTable />
			</DashboardProvider>
		</>
	);
}
